import { encrypt } from "@/services/CryptoService.js";

/**
 * One company, one letter. The scanner refuses a sticker belonging to someone
 * else, and spelling the company out cost more of the printed code than the
 * check is worth: "Bergsoe Metals Co., Ltd" is 23 characters of a payload that
 * has to stay readable from arm's length.
 */
export function ownerToken(companyName) {
  const first = companyName
    ? String(companyName).trim().charAt(0).toUpperCase()
    : "";
  // The reader accepts one letter or digit and nothing else, so a company name
  // that is blank — a mis-set env-config.js is enough — or one that starts with
  // a Thai or Chinese character would otherwise print a label that looks
  // perfect and scans as "unrecognizable QR code".
  return /^[A-Za-z0-9]$/.test(first) ? first : "X";
}

/**
 * The QR payload for a label.
 *
 * Shared by the batch print page and by auto-print. A sticker printed at the
 * bench when the entry is saved and one reprinted from the batch page later have
 * to scan to the same thing, so there is exactly one place that decides what
 * goes into the code.
 *
 * The payload is deliberately terse. It used to be JSON — field names, a type
 * that was always "DATA", a timestamp nothing read — which encrypted to 216
 * characters and printed as a 61-module code: readable, but only with the phone
 * held almost against the label. The same facts as `<owner>|<kind>|<rest>` come
 * to 29-37 modules, which reads from roughly twice the distance.
 *
 * LME products encode the human lot number itself; everything else encodes an
 * encrypted reference, to the inventory for a receiving document and to the
 * entry for every other sticker.
 */
export function buildQrValue({
  worksheet,
  entry,
  lotIndex,
  isLME,
  lmeBrand,
  companyName,
  inventoryDocumentNumber,
}) {
  if (isLME) {
    return `${lmeBrand || ""}|${worksheet.documentNumber}-${lotIndex}`;
  }
  const owner = ownerToken(companyName);
  if (inventoryDocumentNumber) {
    return encrypt(`${owner}|I|${inventoryDocumentNumber}`);
  }
  return encrypt(`${owner}|W|${worksheet.id}|${entry.id}`);
}

const COMPACT_PAYLOAD = /^([A-Za-z0-9])\|([WI])\|(.+)$/;

const asId = (value) => {
  if (value === undefined || value === "") {
    return undefined;
  }
  const asNumber = Number(value);
  return Number.isNaN(asNumber) ? value : asNumber;
};

/**
 * Read a scanned sticker, in either payload.
 *
 * Labels already on pallets carry the old JSON object and will for as long as
 * the goods sit in the yard, so both shapes resolve to the same fields rather
 * than the plant being asked to reprint anything.
 *
 * Returns null for anything that is not one of our stickers — an LME label, a
 * dynamic QR URL, a code from another system — which the caller recognises in
 * its own way.
 */
export function parseQrPayload(scanned) {
  if (typeof scanned === "string") {
    const match = COMPACT_PAYLOAD.exec(scanned);
    if (!match) {
      return null;
    }
    const [, owner, kind, rest] = match;
    if (kind === "I") {
      return { owner, businessType: "Inventory", documentNumber: rest };
    }
    const [worksheetId, entryId] = rest.split("|");
    return {
      owner,
      businessType: "WorksheetEntry",
      worksheetId: asId(worksheetId),
      entryId: asId(entryId),
    };
  }

  if (!scanned || typeof scanned !== "object") {
    return null;
  }
  const businessType = scanned.businessType;
  if (businessType !== "WorksheetEntry" && businessType !== "Inventory") {
    return null;
  }
  return {
    owner: ownerToken(scanned.owner),
    businessType,
    worksheetId: asId(scanned.worksheetId),
    // Stickers encode the entry as `entryId`; `worksheetEntryId` has turned up
    // from other QR sources.
    entryId: asId(scanned.worksheetEntryId || scanned.entryId),
    documentNumber: scanned.documentNumber,
  };
}

/**
 * One sticker per logical entry: FIFO/LIFO splits arrive as several rows
 * sharing an entryGroupId but represent a single weighing, so they collapse
 * into one label with the weights summed.
 */
export function groupWorksheetEntries(entries) {
  if (!entries || entries.length === 0) {
    return [];
  }
  const groups = {};
  entries.forEach((entry) => {
    const groupId = entry.entryGroupId || entry.id;
    if (!groups[groupId]) {
      groups[groupId] = [];
    }
    groups[groupId].push(entry);
  });

  return Object.values(groups).map((groupEntries) => {
    if (groupEntries.length === 1) {
      return groupEntries[0];
    }
    const first = groupEntries[0];
    return {
      ...first,
      netWeight: groupEntries.reduce((sum, e) => sum + (e.netWeight || 0), 0),
      grossWeight: first.grossWeight,
      netAdjustmentWeight: groupEntries.reduce(
        (sum, e) => sum + (e.netAdjustmentWeight || 0),
        0
      ),
    };
  });
}

/**
 * The number printed on the label, as `documentNumber-<n>`. It is a POSITION,
 * not something stored on the entry, so it is only meaningful against the full
 * current entry list — and it changes if an earlier entry is deleted. Returns 0
 * when the entry is not in the list, which callers must treat as "do not print".
 */
export function lotIndexOf(entries, entry) {
  const grouped = groupWorksheetEntries(entries);
  const index = grouped.findIndex((e) => e.id == entry.id);
  return index >= 0 ? index + 1 : 0;
}
